import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import PreviewBuilderLayout from '@/ui/layouts/previewBuilder/PreviewBuilderLayout';
import EstimateBuilderSidebar from '@/ui/pages/estimateBuilderPage/components/EstimateBuilderSidebar';
import { BreadcrumbItem, Card, EmptyState } from '@payaca/ambrosia';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { FC, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import EstimatePreviewPane from './components/EstimatePreviewPane';

type EstimateBuilderContentProps = {
  projectId: string;
  estimateId: string;
  breadcrumbItems: BreadcrumbItem[];
};

const EstimateBuilderContent: FC<EstimateBuilderContentProps> = (props) => {
  const { projectId, estimateId, breadcrumbItems } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  // The active section tab is shared between the editor sidebar and the
  // document preview (which the layout renders as siblings). The preview tags
  // each section's rendered region with a `data-tab-target`; clicking one
  // selects the matching tab, and selecting a tab scrolls its region into view.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    estimate?.sections[0]?.id ?? null
  );

  return (
    <PreviewBuilderLayout
      breadcrumbItems={breadcrumbItems}
      previewButtonLabel="Preview estimate"
      renderEditor={() => (
        <EstimateBuilderSidebar
          projectId={projectId}
          estimateId={estimateId}
          activeSectionId={activeSectionId}
          onSetActiveSectionId={setActiveSectionId}
        />
      )}
      renderPreview={({ isMobile, closePanel }) => (
        <EstimatePreviewPane
          projectId={projectId}
          estimateId={estimateId}
          activeTarget={activeSectionId}
          onSelectTab={(target) => {
            setActiveSectionId(target);
            // On mobile the preview is a sheet over the editor, so close it to
            // reveal the now-active section.
            if (isMobile) closePanel();
          }}
        />
      )}
    />
  );
};

const EstimateBuilderPage: FC = () => {
  const { projectId, estimateId } = useParams<{
    projectId: string;
    estimateId: string;
  }>();
  const { data: features, isLoading: isLoadingFeatures } =
    useGetMyAccountFeatures();
  const hasEstimates = features?.includes('hasEstimates') ?? false;

  const { project, estimate, isLoading, isFetching } = useGetProjectEstimate(
    hasEstimates ? projectId : undefined,
    estimateId
  );

  // Estimates are gated on hasEstimates, mirroring the gate on the
  // Estimates tab in ProjectPage. Backend access policies enforce this
  // independently — the redirect avoids rendering an unusable page UI.
  if (!isLoadingFeatures && !hasEstimates) {
    return <Redirect to={`/projects/${projectId}`} />;
  }

  // Always render three crumbs so the trail length doesn't shift when
  // project metadata arrives. Crumbs without a `to` render as plain text
  // labels.
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: project?.pipeline.name ?? 'Pipeline',
      to: project?.pipeline
        ? `/pipelines/${project.pipeline.publicId}`
        : undefined,
    },
    {
      label: project ? `Project: #${project.reference}` : 'Project',
      to: `/projects/${projectId}/estimates`,
    },
    {
      label: estimate ? `Estimate: ${estimate.reference}` : 'Estimate',
    },
  ];

  // Show the loader on the initial fetch and on background refetches that
  // haven't yet resolved the estimate. The latter avoids briefly flashing
  // "not found" after a cache-invalidating mutation (e.g. just-created
  // estimate not yet present in cache).
  if (isLoadingFeatures || isLoading || (isFetching && !estimate)) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <LoaderOverlay />
      </AuthenticatedPageWrapper>
    );
  }

  if (!estimate) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <Card>
          <Card.Body>
            <EmptyState text="Estimate not found." />
          </Card.Body>
        </Card>
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <EstimateBuilderContent
      projectId={projectId}
      estimateId={estimate.id}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default EstimateBuilderPage;
